import {
    Banner,
    BlockStack,
    Button,
    Card,
    InlineStack,
    Layout,
    Modal,
    Page,
    SkeletonBodyText,
    Text,
    TextField,
} from '@shopify/polaris';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import agent from '../../app/api/agent';
import { getApiErrorMessage } from '../../app/api/error';
import { LegacyVehicleLevel, LegacyVehicleMutation } from '../../app/models/LegacyMode';

const validLevels = new Set(['make', 'model', 'submodel', 'year']);

const LegacyVehiclesPage = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const requestedLevel = searchParams.get('level') || 'make';
    const level = validLevels.has(requestedLevel) ? requestedLevel : 'make';
    const make = searchParams.get('make') || '';
    const model = searchParams.get('model') || '';
    const submodel = searchParams.get('submodel') || '';
    const [data, setData] = useState<LegacyVehicleLevel>();
    const [drafts, setDrafts] = useState<Record<string, string>>({});
    const [newValue, setNewValue] = useState('');
    const [loading, setLoading] = useState(true);
    const [busyKey, setBusyKey] = useState('');
    const [deleteValue, setDeleteValue] = useState('');
    const [error, setError] = useState('');

    const load = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const result = await agent.LegacyAdmin.vehicles({ level, make, model, submodel });
            setData(result);
            setDrafts(Object.fromEntries(result.rows.map(row => [row.value, row.value])));
        } catch (currentError) {
            setError(await getApiErrorMessage(currentError, 'Unable to load legacy vehicles.'));
        } finally {
            setLoading(false);
        }
    }, [level, make, model, submodel]);

    useEffect(() => { load(); }, [load]);

    const baseMutation = useMemo<Omit<LegacyVehicleMutation, 'value'>>(() => ({
        level,
        make: make || undefined,
        model: model || undefined,
        submodel: submodel || undefined,
    }), [level, make, model, submodel]);

    const childUrl = (value: string) => {
        if (level === 'make') return `/legacy/vehicles?level=model&make=${encodeURIComponent(value)}`;
        if (level === 'model' && data?.submodelEnabled) return `/legacy/vehicles?level=submodel&make=${encodeURIComponent(make)}&model=${encodeURIComponent(value)}`;
        if (level === 'model') return `/legacy/vehicles?level=year&make=${encodeURIComponent(make)}&model=${encodeURIComponent(value)}`;
        if (level === 'submodel') return `/legacy/vehicles?level=year&make=${encodeURIComponent(make)}&model=${encodeURIComponent(model)}&submodel=${encodeURIComponent(value)}`;
        return '';
    };

    const run = async (key: string, action: () => Promise<unknown>) => {
        setBusyKey(key);
        setError('');
        try {
            await action();
            await load();
        } catch (currentError) {
            setError(await getApiErrorMessage(currentError, 'Unable to update the vehicle hierarchy.'));
        } finally {
            setBusyKey('');
        }
    };

    const breadcrumbs = [
        { label: 'Makes', onClick: () => navigate('/legacy/vehicles') },
        ...(make ? [{ label: make, onClick: () => navigate(`/legacy/vehicles?level=model&make=${encodeURIComponent(make)}`) }] : []),
        ...(model ? [{ label: model, onClick: () => navigate(`/legacy/vehicles?level=${data?.submodelEnabled ? 'submodel' : 'year'}&make=${encodeURIComponent(make)}&model=${encodeURIComponent(model)}`) }] : []),
        ...(submodel ? [{ label: submodel, onClick: () => navigate(`/legacy/vehicles?level=year&make=${encodeURIComponent(make)}&model=${encodeURIComponent(model)}&submodel=${encodeURIComponent(submodel)}`) }] : []),
    ];

    return (
        <Page title={`${level.charAt(0).toUpperCase()}${level.slice(1)}s`} subtitle="Manage the vehicle hierarchy used by the legacy storefront selector.">
            <Layout>
                {error && <Layout.Section><Banner tone="critical"><p>{error}</p></Banner></Layout.Section>}
                <Layout.Section>
                    <Card>
                        <BlockStack gap="400">
                            <InlineStack gap="200" wrap>
                                {breadcrumbs.map((item, index) => (
                                    <InlineStack key={`${item.label}-${index}`} gap="200">
                                        {index > 0 && <Text as="span" tone="subdued">/</Text>}
                                        <Button variant="plain" onClick={item.onClick}>{item.label}</Button>
                                    </InlineStack>
                                ))}
                            </InlineStack>

                            {loading ? <SkeletonBodyText lines={8} /> : data?.rows.length ? (
                                <BlockStack gap="200">
                                    {data.rows.map(row => (
                                        <div key={row.value} style={{ borderTop: '1px solid #e1e3e5', padding: '14px 0' }}>
                                            <InlineStack gap="300" blockAlign="center" wrap={false}>
                                                <div style={{ flex: 1 }}>
                                                    <TextField
                                                        label={`${level} ${row.value}`}
                                                        labelHidden
                                                        value={drafts[row.value] ?? row.value}
                                                        onChange={value => setDrafts(current => ({ ...current, [row.value]: value }))}
                                                        autoComplete="off"
                                                    />
                                                </div>
                                                {level !== 'year' && (
                                                    <Button onClick={() => navigate(childUrl(row.value))}>
                                                        {`${row.childCount} ${level === 'make' ? 'models' : level === 'model' && data.submodelEnabled ? 'submodels' : 'years'}`}
                                                    </Button>
                                                )}
                                                <Button
                                                    loading={busyKey === `save-${row.value}`}
                                                    onClick={() => run(`save-${row.value}`, () => agent.LegacyAdmin.updateVehicle({
                                                        ...baseMutation,
                                                        originalValue: row.value,
                                                        value: drafts[row.value] ?? row.value,
                                                    }))}
                                                >Save</Button>
                                                <Button tone="critical" onClick={() => setDeleteValue(row.value)}>Delete</Button>
                                            </InlineStack>
                                        </div>
                                    ))}
                                </BlockStack>
                            ) : <Text as="p" tone="subdued">No {level}s have been added yet.</Text>}

                            <div style={{ borderTop: '1px solid #e1e3e5', paddingTop: 18 }}>
                                <BlockStack gap="300">
                                    <TextField
                                        label={`New ${level}`}
                                        value={newValue}
                                        onChange={setNewValue}
                                        autoComplete="off"
                                        helpText={level === 'year' ? 'Enter one year or a range such as 2018-2024.' : undefined}
                                    />
                                    <InlineStack align="start">
                                        <Button
                                            variant="primary"
                                            disabled={!newValue.trim()}
                                            loading={busyKey === 'add'}
                                            onClick={() => run('add', async () => {
                                                await agent.LegacyAdmin.addVehicle({ ...baseMutation, value: newValue });
                                                setNewValue('');
                                            })}
                                        >Add {level}</Button>
                                    </InlineStack>
                                </BlockStack>
                            </div>
                        </BlockStack>
                    </Card>
                </Layout.Section>
            </Layout>

            <Modal
                open={!!deleteValue}
                onClose={() => setDeleteValue('')}
                title={`Delete ${deleteValue}?`}
                primaryAction={{
                    content: 'Delete',
                    destructive: true,
                    loading: busyKey === `delete-${deleteValue}`,
                    onAction: () => run(`delete-${deleteValue}`, async () => {
                        await agent.LegacyAdmin.deleteVehicle({ ...baseMutation, value: deleteValue });
                        setDeleteValue('');
                    }),
                }}
                secondaryActions={[{ content: 'Cancel', onAction: () => setDeleteValue('') }]}
            >
                <Modal.Section>
                    <Text as="p">Deleting this value also removes the vehicle rows below it. Existing Shopify product tags are not removed automatically.</Text>
                </Modal.Section>
            </Modal>
        </Page>
    );
};

export default LegacyVehiclesPage;
